import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users, UserPlus, Trash2, Crown, Edit, Eye } from 'lucide-react'
import { toast } from 'sonner'

import { socialMediaCalendarsApi, usersApi } from '@/lib/api'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { CalendarMember, CalendarMemberRole, UserListItem } from '@/types'

interface CalendarSharingDialogProps {
  calendarId: string
  calendarName: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ROLE_CONFIG: Record<CalendarMemberRole, { label: string; icon: React.ReactNode; description: string }> = {
  OWNER: { label: 'Owner', icon: <Crown className="h-3 w-3" />, description: 'Full access, can manage members' },
  EDITOR: { label: 'Editor', icon: <Edit className="h-3 w-3" />, description: 'Can create and edit posts' },
  VIEWER: { label: 'Viewer', icon: <Eye className="h-3 w-3" />, description: 'Can only view posts' },
}

export function CalendarSharingDialog({ calendarId, calendarName, open, onOpenChange }: CalendarSharingDialogProps) {
  const queryClient = useQueryClient()
  const [selectedUserId, setSelectedUserId] = useState<string>('')
  const [selectedRole, setSelectedRole] = useState<CalendarMemberRole>('VIEWER')

  const { data: members = [], isLoading: membersLoading } = useQuery({
    queryKey: ['calendarMembers', calendarId],
    queryFn: () => socialMediaCalendarsApi.getMembers(calendarId),
    enabled: open && !!calendarId,
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
    enabled: open,
  })

  // Filter out users who are already members
  const availableUsers = users.filter(
    (user: UserListItem) => !members.some((member: CalendarMember) => member.user_id === user.user_id)
  )

  const addMemberMutation = useMutation({
    mutationFn: (data: { user_id: string; member_role: CalendarMemberRole }) =>
      socialMediaCalendarsApi.addMember(calendarId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarMembers', calendarId] })
      toast.success('Member added successfully')
      setSelectedUserId('')
      setSelectedRole('VIEWER')
    },
    onError: () => toast.error('Failed to add member'),
  })

  const updateMemberMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: CalendarMemberRole }) =>
      socialMediaCalendarsApi.updateMember(calendarId, userId, { member_role: role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarMembers', calendarId] })
      toast.success('Member role updated')
    },
    onError: () => toast.error('Failed to update member role'),
  })

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => socialMediaCalendarsApi.removeMember(calendarId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendarMembers', calendarId] })
      toast.success('Member removed')
    },
    onError: () => toast.error('Failed to remove member'),
  })

  const handleAddMember = () => {
    if (!selectedUserId) return
    addMemberMutation.mutate({ user_id: selectedUserId, member_role: selectedRole })
  }

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Share Calendar
          </DialogTitle>
          <DialogDescription>
            Manage who has access to "{calendarName}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add Member Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Add Member</h4>
            <div className="flex gap-2">
              <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a user..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((user: UserListItem) => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.full_name} ({user.user_email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v as CalendarMemberRole)}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EDITOR">Editor</SelectItem>
                  <SelectItem value="VIEWER">Viewer</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleAddMember} disabled={!selectedUserId || addMemberMutation.isPending}>
                <UserPlus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Members List */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Members ({members.length})</h4>
            {membersLoading ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-muted-foreground">No members yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {members.map((member: CalendarMember) => (
                  <MemberRow
                    key={member.member_id}
                    member={member}
                    getInitials={getInitials}
                    onUpdateRole={(role) => updateMemberMutation.mutate({ userId: member.user_id, role })}
                    onRemove={() => removeMemberMutation.mutate(member.user_id)}
                    isUpdating={updateMemberMutation.isPending}
                    isRemoving={removeMemberMutation.isPending}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

interface MemberRowProps {
  member: CalendarMember
  getInitials: (name: string) => string
  onUpdateRole: (role: CalendarMemberRole) => void
  onRemove: () => void
  isUpdating: boolean
  isRemoving: boolean
}

function MemberRow({ member, getInitials, onUpdateRole, onRemove, isUpdating, isRemoving }: MemberRowProps) {
  const isOwner = member.member_role === 'OWNER'
  const roleConfig = ROLE_CONFIG[member.member_role]

  return (
    <div className="flex items-center justify-between p-2 rounded-lg border bg-card">
      <div className="flex items-center gap-3">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-xs">{getInitials(member.full_name)}</AvatarFallback>
        </Avatar>
        <div>
          <p className="text-sm font-medium">{member.full_name}</p>
          <p className="text-xs text-muted-foreground">{member.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {isOwner ? (
          <Badge variant="secondary" className="gap-1">
            {roleConfig.icon}
            {roleConfig.label}
          </Badge>
        ) : (
          <>
            <Select
              value={member.member_role}
              onValueChange={(v) => onUpdateRole(v as CalendarMemberRole)}
              disabled={isUpdating}
            >
              <SelectTrigger className="w-28 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EDITOR">Editor</SelectItem>
                <SelectItem value="VIEWER">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={onRemove}
              disabled={isRemoving}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

